// Public set-password screen shared by /accept-invite and /reset-password.
// Mirrors LoginPage.tsx: react-hook-form + zod + useMutation + ApiError
// mapping, same centered-panel styling. Token arrives as a validated search
// param; both endpoints are public (no session, no CSRF — same trust model
// as login) and answer a uniform 400 for invalid/expired tokens.

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { z } from 'zod';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { ApiError } from '@/api/errors';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';

// Mirrors shared::validation::validate_password (PASSWORD_MIN_LEN=10 / MAX=128).
const schema = z
  .object({
    password: z
      .string()
      .min(10, 'Password must be at least 10 characters')
      .max(128, 'Password must be at most 128 characters'),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: 'Passwords don’t match',
  });

type FormValues = z.infer<typeof schema>;

export type SetPasswordMode = 'invite' | 'reset';

const COPY: Record<SetPasswordMode, {
  title: string;
  sub: string;
  endpoint: string;
  cta: string;
  pending: string;
  resend: string;
}> = {
  invite: {
    title: 'Accept your invite',
    sub: 'Choose a password to finish setting up your account.',
    endpoint: '/auth/accept-invite',
    cta: 'Set password',
    pending: 'Setting password…',
    resend: 'Ask an admin to resend your invite.',
  },
  reset: {
    title: 'Reset your password',
    sub: 'Choose a new password for your account.',
    endpoint: '/auth/reset-password',
    cta: 'Reset password',
    pending: 'Resetting password…',
    resend: 'Ask an admin to send a new reset link.',
  },
};

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="auth-shell" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
      <div className="panel" style={{ width: 360, padding: 22 }}>
        <h1 className="page-title" style={{ fontSize: 22, marginBottom: 4 }}>SHUBERT ADS MANAGER</h1>
        {children}
      </div>
    </div>
  );
}

export function SetPasswordPage({ mode, token }: { mode: SetPasswordMode; token: string }) {
  const copy = COPY[mode];
  const required = useRequiredFeedback();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    shouldFocusError: false,
  });

  const mutation = useRegisteredMutation({
    // No data effect, on purpose. This screen has no session — accept-invite
    // and reset-password are public, and the user signs in afterwards — so a
    // declared domain only sent the coordinator to GET /data-revisions, which
    // can answer nothing but 401. That 401 then broadcast an RBAC purge to
    // every sibling tab in the browser, reloading an operator's screen because
    // a colleague set a password. The session that follows starts its own
    // manifest baseline at login.
    noDataEffect: true,
    mutationFn: (v: FormValues) =>
      api.post<{ ok: boolean }>(copy.endpoint, { body: { token, password: v.password } }),
  });

  if (!token) {
    return (
      <Panel>
        <div className="muted" style={{ fontSize: 12, marginBottom: 14 }}>{copy.title}</div>
        <div style={{ fontSize: 12.5, color: 'var(--bad)', marginBottom: 6 }}>
          This link is missing its token.
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          Open the link from your email exactly as it was sent — or {copy.resend.charAt(0).toLowerCase() + copy.resend.slice(1)}
        </div>
      </Panel>
    );
  }

  if (mutation.isSuccess) {
    return (
      <Panel>
        <div className="muted" style={{ fontSize: 12, marginBottom: 14 }}>{copy.title}</div>
        <div style={{ fontSize: 12.5, marginBottom: 14 }}>
          Your password is set. Sign in with it to continue.
        </div>
        <Link to="/login" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
          Go to sign in
        </Link>
      </Panel>
    );
  }

  const serverError =
    mutation.error instanceof ApiError
      ? mutation.error.status === 400
        ? `This link is invalid or has expired. ${copy.resend}`
        : mutation.error.status === 409
          ? 'This link has already been used. If that wasn’t you, ask an admin for a new one.'
          : mutation.error.message
      : mutation.error
        ? 'Request failed — check your connection and try again.'
        : null;

  return (
    <Panel>
      <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>{copy.title}</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 18 }}>{copy.sub}</div>
      <form
        onSubmit={handleSubmit(
          (values) => {
            required.clearAll();
            mutation.mutate(values);
          },
          (formErrors) => {
            required.report([
              formErrors.password && {
                key: 'set-password-password',
                message: String(formErrors.password.message || 'Enter a valid password'),
              },
              formErrors.confirm && {
                key: 'set-password-confirm',
                message: String(formErrors.confirm.message || 'Passwords don’t match'),
              },
            ]);
          },
        )}
      >
        <RequiredField
          fieldKey="set-password-password"
          message={required.messages['set-password-password'] || errors.password?.message}
          attempt={required.attempt}
          className="field"
        >
          <label className="field-lbl" htmlFor="password">New password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            className="input"
            data-validation-focus
            {...register('password', {
              onChange: (event) => {
                const next = String(event.target.value);
                if (next.length >= 10 && next.length <= 128) {
                  required.clear('set-password-password');
                }
                if (next === getValues('confirm')) {
                  required.clear('set-password-confirm');
                }
              },
            })}
          />
        </RequiredField>

        <RequiredField
          fieldKey="set-password-confirm"
          message={required.messages['set-password-confirm'] || errors.confirm?.message}
          attempt={required.attempt}
          className="field"
        >
          <label className="field-lbl" htmlFor="confirm">Confirm password</label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            className="input"
            data-validation-focus
            {...register('confirm', {
              onChange: (event) => {
                if (event.target.value && event.target.value === getValues('password')) {
                  required.clear('set-password-confirm');
                }
              },
            })}
          />
        </RequiredField>

        {serverError && <div className="field-hint" style={{ color: 'var(--bad)', marginTop: 8 }}>{serverError}</div>}

        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          className="btn btn-primary"
          style={{ marginTop: 14, width: '100%', justifyContent: 'center' }}
        >
          {mutation.isPending ? copy.pending : copy.cta}
        </button>
      </form>
    </Panel>
  );
}
